import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCreateTask } from "@/lib/useTaskMutations";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useEffortUnit } from "@/lib/effort";
import { Field, Input, Select } from "./Field";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { AssigneeCombobox } from "./AssigneeCombobox";
import { RichTextEditor } from "./RichTextEditor";
import { TagChipInput, mergePendingTag } from "./TagChipInput";
import type { TaskStatus, WorkItemType } from "@shared/types";
import { typeMeta } from "@/lib/workItemType";

// ---------------------------------------------------------------------------
// Draft helpers
// ---------------------------------------------------------------------------

function draftKey(projectId: string) {
  return `task-draft:${projectId}`;
}

interface DraftState {
  title: string;
  description: string;
  priority: number;
  status: TaskStatus;
  type: WorkItemType;
  dueDate: string;
  startDate: string;
  estimate: string;
  subtasks: string[];
  assigneeIds: string[];
  /** Tag names entered as chips (free text — may not exist server-side yet). */
  tagNames: string[];
}

function loadDraft(projectId: string): Partial<DraftState> | null {
  try {
    const raw = localStorage.getItem(draftKey(projectId));
    return raw ? (JSON.parse(raw) as Partial<DraftState>) : null;
  } catch {
    return null;
  }
}

function saveDraft(projectId: string, state: DraftState) {
  try {
    localStorage.setItem(draftKey(projectId), JSON.stringify(state));
  } catch {
    /* ignore — private mode or quota; draft is best-effort */
  }
}

function clearDraft(projectId: string) {
  try {
    localStorage.removeItem(draftKey(projectId));
  } catch {
    /* ignore — private mode or quota; draft is best-effort */
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskCreateInput {
  title: string;
  priority?: number;
  status?: TaskStatus;
  type?: WorkItemType;
  parentTaskId?: string | null;
  description?: string;
  dueDate?: string | null;
  startDate?: string | null;
  estimate?: number | null;
  /**
   * The server's task-create endpoint accepts a single `assigneeId`; if
   * the UI captured multiple selections we send the first and remember
   * the rest via `extraAssigneeIds` so the caller can assign them after
   * create (no batch endpoint exists yet).
   */
  assigneeId?: string | null;
  extraAssigneeIds?: string[];
  /**
   * Free-text tag names entered as chips. The caller resolves each name
   * to a tag id (creating new tags as needed) before posting to
   * /api/tasks/:id/tags. We deliberately surface names rather than ids so
   * the user can type something new without round-tripping through a
   * tag-create modal.
   */
  tagNames?: string[];
  /**
   * Non-empty subtask titles from the "break it down" rows. The create
   * endpoint has no nested-create, so the caller posts each as a child
   * task (parentTaskId) after the parent lands. Previously these rows
   * were silently discarded on submit.
   */
  subtaskTitles?: string[];
  /**
   * Files picked/dropped on the Attachments dropzone. Uploads need a task
   * id, so the caller uploads them after create. Files can't ride the
   * localStorage draft — they only survive within the live form session.
   */
  files?: File[];
}

export interface TaskFormProps {
  projectId: string;
  parentTaskId?: string;
  /** "inline" keeps the existing compact form; "page" renders the full-page 2-column layout */
  mode?: "inline" | "page";
  /**
   * Seed for the status pill. Used when the caller knows which column the
   * user was on (e.g. "+ Add task" inside a Kanban column). Overrides the
   * saved draft so the column the user clicked wins.
   */
  initialStatus?: TaskStatus;
  /** page-mode: called after successful submit */
  onSubmit?: (payload: TaskCreateInput) => void;
  /** page-mode: called when Cancel is clicked */
  onCancel?: () => void;
  /** inline-mode: called after task is created */
  onCreated?: () => void;
}

// ---------------------------------------------------------------------------
// Pill picker helper
// ---------------------------------------------------------------------------

interface PillOption<T extends string> {
  value: T;
  label: string;
  dot?: string;
}

function PillPicker<T extends string>({
  options,
  value,
  onChange,
}: {
  options: PillOption<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="pick">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-v={o.value}
          className={`p${value === o.value ? " on" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.dot ? (
            <span className="dot" style={{ background: o.dot }} />
          ) : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

const STATUS_OPTIONS: PillOption<TaskStatus>[] = [
  { value: "backlog", label: "Backlog", dot: "#94a0b4" },
  { value: "todo", label: "To Do", dot: "#3b82d4" },
  { value: "doing", label: "Doing", dot: "#e8612c" },
  { value: "done", label: "Done", dot: "#1f8a5b" },
];

const TYPE_OPTIONS: PillOption<WorkItemType>[] = [
  { value: "task", label: "Task", dot: typeMeta("task").color },
  { value: "story", label: "Story", dot: typeMeta("story").color },
  { value: "bug", label: "Bug", dot: typeMeta("bug").color },
];

const PRIORITY_OPTIONS: PillOption<string>[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

// Map UI priority value <-> numeric priority used by the API.
const PRIORITY_TO_NUM: Record<string, number> = {
  urgent: 1,
  high: 2,
  medium: 3,
  low: 4,
};
const NUM_TO_PRIORITY: Record<number, string> = {
  1: "urgent",
  2: "high",
  3: "medium",
  4: "low",
};

// Real assignees + tags now flow from /api/team and /api/tags. The page-mode
// project selector is dropped — the route already pins the project via the
// URL param, so a selector here would only confuse.

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TaskForm({
  projectId,
  parentTaskId,
  mode = "inline",
  initialStatus,
  onSubmit,
  onCancel,
  onCreated,
}: TaskFormProps) {
  // Load once — loadDraft on every render would re-parse localStorage and
  // fight the discard flow.
  const [draft] = useState(() =>
    mode === "page" ? loadDraft(projectId) : null,
  );

  const [title, setTitle] = useState(draft?.title ?? "");
  const [priority, setPriority] = useState(draft?.priority ?? 3);
  const [status, setStatus] = useState<TaskStatus>(
    initialStatus ?? draft?.status ?? "backlog",
  );
  const [description, setDescription] = useState(draft?.description ?? "");
  const [dueDate, setDueDate] = useState(draft?.dueDate ?? "");
  const [startDate, setStartDate] = useState(draft?.startDate ?? "");
  const [estimate, setEstimate] = useState(draft?.estimate ?? "");
  const [type, setType] = useState<WorkItemType>(draft?.type ?? "task");
  const effortUnit = useEffortUnit();
  const [assigneeIds, setAssigneeIds] = useState<string[]>(
    draft?.assigneeIds ?? [],
  );
  const [tagNames, setTagNames] = useState<string[]>(draft?.tagNames ?? []);
  const [tagDraft, setTagDraft] = useState("");
  const [subtasks, setSubtasks] = useState<string[]>(draft?.subtasks ?? [""]);
  // Dropzone files (page mode). Not part of the draft — File objects don't
  // serialize to localStorage.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Page-mode pickers are populated from the real team + tag endpoints so
  // the user only picks options the server can resolve.
  const team = useQuery({
    queryKey: queryKeys.team(),
    queryFn: api.listTeam,
    enabled: mode === "page",
  });
  const tags = useQuery({
    queryKey: queryKeys.tags(),
    queryFn: api.listTags,
    enabled: mode === "page",
  });
  const project = useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: () => api.getProject(projectId),
    enabled: mode === "page",
  });
  // T12: pre-select the current user as the default assignee on a fresh
  // form (no draft, no explicit pick yet). Once the user toggles anyone,
  // we leave their selection alone.
  const me = useQuery({
    queryKey: queryKeys.me(),
    queryFn: api.me,
    enabled: mode === "page",
  });
  const didDefaultAssignee = useRef(false);
  useEffect(() => {
    if (mode !== "page") return;
    if (didDefaultAssignee.current) return;
    if (!me.data?.user?.id) return;
    // Don't override a draft-restored selection or anything the user has
    // already toggled.
    if (assigneeIds.length > 0) {
      didDefaultAssignee.current = true;
      return;
    }
    setAssigneeIds([me.data.user.id]);
    didDefaultAssignee.current = true;
  }, [mode, me.data?.user?.id, assigneeIds.length]);

  // T44: drafts autosave on change (debounced) instead of via an explicit
  // "Save draft" button — navigating away, closing the tab, or crashing no
  // longer loses an in-progress task. A draft with no real content is
  // cleared rather than saved so pristine visits never trigger the
  // restore notice (the auto-assignee default alone isn't worth keeping).
  const hasContent =
    title.trim() !== "" ||
    description.trim() !== "" ||
    dueDate !== "" ||
    startDate !== "" ||
    estimate !== "" ||
    subtasks.some((s) => s.trim() !== "") ||
    tagNames.length > 0 ||
    tagDraft.trim() !== "";
  const [restored, setRestored] = useState(
    () =>
      !!draft &&
      !!(
        (draft.title ?? "").trim() ||
        (draft.description ?? "").trim() ||
        (draft.dueDate ?? "") !== "" ||
        (draft.startDate ?? "") !== "" ||
        (draft.estimate ?? "") !== "" ||
        (draft.subtasks ?? []).some((s) => s.trim()) ||
        (draft.tagNames ?? []).length
      ),
  );
  const skipFirstAutosave = useRef(true);
  useEffect(() => {
    if (mode !== "page") return;
    if (skipFirstAutosave.current) {
      skipFirstAutosave.current = false;
      return;
    }
    const t = setTimeout(() => {
      if (!hasContent) {
        clearDraft(projectId);
        return;
      }
      const names = mergePendingTag(tagNames, tagDraft);
      saveDraft(projectId, {
        title,
        description,
        priority,
        status,
        type,
        dueDate,
        startDate,
        estimate,
        subtasks,
        assigneeIds,
        tagNames: names,
      });
    }, 800);
    return () => clearTimeout(t);
  }, [
    mode,
    projectId,
    title,
    description,
    priority,
    status,
    type,
    dueDate,
    startDate,
    estimate,
    subtasks,
    assigneeIds,
    tagNames,
    tagDraft,
  ]);

  function discardDraft() {
    clearDraft(projectId);
    setTitle("");
    setDescription("");
    setPriority(3);
    setStatus(initialStatus ?? "backlog");
    setDueDate("");
    setSubtasks([""]);
    setTagNames([]);
    setTagDraft("");
    // Re-run the default-assignee effect so the fresh form gets "me" again.
    didDefaultAssignee.current = false;
    setAssigneeIds([]);
    setRestored(false);
  }

  // inline-mode mutation
  const mut = useCreateTask(projectId, {
    onSuccess: () => {
      setTitle("");
      onCreated?.();
    },
  });

  // ---------------------------------------------------------------------------
  // Inline mode (existing compact form — unchanged behaviour)
  // ---------------------------------------------------------------------------
  if (mode === "inline") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const t = title.trim();
          if (!t) return;
          mut.mutate({
            title: t,
            priority,
            status,
            ...(parentTaskId ? { parentTaskId } : {}),
          });
        }}
        style={{ display: "grid", gap: 12 }}
      >
        <Field label="Title" id="task-title">
          <Input
            id="task-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={parentTaskId ? "New subtask…" : "New task…"}
            required
            autoFocus
          />
        </Field>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <Field label="Priority" id="task-pri">
            <Select
              id="task-pri"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            >
              <option value={1}>Urgent</option>
              <option value={2}>High</option>
              <option value={3}>Medium</option>
              <option value={4}>Low</option>
            </Select>
          </Field>
          <Field label="Status" id="task-status">
            <Select
              id="task-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
            >
              <option value="backlog">Backlog</option>
              <option value="todo">To Do</option>
              <option value="doing">Doing</option>
              <option value="done">Done</option>
            </Select>
          </Field>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="submit" variant="primary" disabled={mut.isPending}>
            {mut.isPending ? "Adding…" : "Add task"}
          </Button>
        </div>
      </form>
    );
  }

  // ---------------------------------------------------------------------------
  // Page mode — full 2-column layout
  // ---------------------------------------------------------------------------

  function handleSubmit() {
    const t = title.trim();
    if (!t) return;
    // Flush any text still sitting in the chip-input — otherwise a user
    // who types "blocker" and clicks Create without pressing comma loses it.
    const finalTagNames = mergePendingTag(tagNames, tagDraft);
    clearDraft(projectId);
    const [firstAssignee, ...extraAssignees] = assigneeIds;
    onSubmit?.({
      title: t,
      description: description.trim() || undefined,
      priority,
      status,
      type,
      dueDate: dueDate || null,
      startDate: startDate || null,
      estimate: estimate.trim() === "" ? null : Number(estimate),
      // Server's create endpoint only accepts a single assigneeId; we surface
      // the rest separately so the caller can post-assign if it wants to.
      assigneeId: firstAssignee ?? null,
      extraAssigneeIds: extraAssignees,
      tagNames: finalTagNames,
      subtaskTitles: subtasks.map((s) => s.trim()).filter(Boolean),
      files: pendingFiles,
      ...(parentTaskId ? { parentTaskId } : {}),
    });
  }

  function addSubtask() {
    setSubtasks((prev) => [...prev, ""]);
  }

  function updateSubtask(i: number, val: string) {
    setSubtasks((prev) => prev.map((s, idx) => (idx === i ? val : s)));
  }

  function removeSubtask(i: number) {
    setSubtasks((prev) => prev.filter((_, idx) => idx !== i));
  }

  const priorityValue = NUM_TO_PRIORITY[priority] ?? "medium";

  return (
    <div className="pg-wrap pg-newtask">
      {/* Top action bar */}
      <div className="nt-top">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary btn-sm"
        >
          <Icon name="arrow" style={{ transform: "rotate(180deg)" }} />
          Cancel
        </button>
        <span className="id">New task</span>
        <div className="sp" />
        <button
          type="button"
          onClick={handleSubmit}
          className="btn btn-primary btn-sm"
        >
          <Icon name="check" />
          Create task
        </button>
      </div>

      {restored && (
        <div className="draft-notice" role="status">
          <span>
            Draft restored — your changes save automatically as you type.
          </span>
          <button type="button" className="dn-discard" onClick={discardDraft}>
            Discard draft
          </button>
          <button
            type="button"
            className="dn-close"
            aria-label="Dismiss"
            onClick={() => setRestored(false)}
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      )}

      {/* 2-column grid */}
      <div className="nt-grid">
        {/* Left main column */}
        <div className="nt-main">
          <input
            className="title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (
                  document.getElementById(
                    "nt-desc",
                  ) as HTMLTextAreaElement | null
                )?.focus();
              }
            }}
            placeholder="Task title…"
            autoFocus
            aria-label="Task title"
          />

          <div className="blk">
            <label htmlFor="nt-desc">
              Description <span className="opt">optional</span>
            </label>
            <RichTextEditor
              id="nt-desc"
              ariaLabel="Description"
              value={description}
              onChange={setDescription}
              upload={{ projectId }}
              minRows={5}
              placeholder="Add detail, context, links… paste an image to attach it"
            />
          </div>

          <div className="blk">
            <label>
              Subtasks <span className="opt">break it down</span>
            </label>
            <div>
              {subtasks.map((st, i) => (
                <div key={i} className="subtask-new">
                  <div className="cb" />
                  <input
                    value={st}
                    onChange={(e) => updateSubtask(i, e.target.value)}
                    placeholder="Add a subtask…"
                    aria-label={`Subtask ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="rm"
                    onClick={() => removeSubtask(i)}
                    aria-label="Remove subtask"
                  >
                    <Icon name="close" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="add-sub" onClick={addSubtask}>
              <Icon name="plus" />
              Add subtask
            </button>
          </div>

          <div className="blk">
            <label>
              Attachments <span className="opt">optional</span>
            </label>
            <div
              className={`dropzone${dragOver ? " drag-over" : ""}`}
              role="button"
              tabIndex={0}
              aria-label="Add attachments"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                // Snapshot before setState: the updater runs at render time,
                // after the DataTransfer has been neutered.
                const dropped = Array.from(e.dataTransfer.files);
                if (dropped.length) {
                  setPendingFiles((prev) => [...prev, ...dropped]);
                }
              }}
            >
              <div className="dz-ic">
                <Icon name="clip" />
              </div>
              <div className="dz-t">Drop files here or click to browse</div>
              <div className="dz-s">
                PDF, images, docs — up to 25 MB each. Uploaded when the task is
                created.
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                // Snapshot before resetting value — clearing the input also
                // empties e.target.files, and the setState updater runs later.
                const picked = Array.from(e.target.files ?? []);
                if (picked.length) {
                  setPendingFiles((prev) => [...prev, ...picked]);
                }
                e.target.value = "";
              }}
            />
            {pendingFiles.length > 0 && (
              <ul className="dz-files">
                {pendingFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="dz-file">
                    <Icon name="clip" size={12} />
                    <span className="dz-file-name">{f.name}</span>
                    <button
                      type="button"
                      className="rm"
                      aria-label={`Remove file ${f.name}`}
                      onClick={() =>
                        setPendingFiles((prev) =>
                          prev.filter((_, idx) => idx !== i),
                        )
                      }
                    >
                      <Icon name="close" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right aside */}
        <aside className="nt-aside">
          <div className="prop-card">
            <div className="pfield">
              <label>
                <Icon name="folder" />
                Project
              </label>
              {/* Pinned by the URL param — no selector needed. */}
              <div className="t-sm t-muted">
                {project.data?.project.name ?? projectId}
              </div>
            </div>

            <div className="pfield">
              <label>
                <Icon name="grid" />
                Type
              </label>
              <PillPicker
                options={TYPE_OPTIONS}
                value={type}
                onChange={setType}
              />
            </div>

            <div className="pfield">
              <label>
                <Icon name="board" />
                Status
              </label>
              <PillPicker
                options={STATUS_OPTIONS}
                value={status}
                onChange={setStatus}
              />
            </div>

            <div className="pfield">
              <label>
                <Icon name="flag" />
                Priority
              </label>
              <PillPicker
                options={PRIORITY_OPTIONS}
                value={priorityValue}
                onChange={(v) => setPriority(PRIORITY_TO_NUM[v] ?? 3)}
              />
            </div>

            <div className="pfield">
              <label>
                <Icon name="users" />
                Assignee
              </label>
              <AssigneeCombobox
                members={team.data?.members ?? []}
                loading={team.isLoading}
                value={assigneeIds[0] ?? null}
                onChange={(id) => setAssigneeIds(id ? [id] : [])}
              />
            </div>

            <div className="pfield">
              <label htmlFor="nt-start">
                <Icon name="cal" />
                Start date
              </label>
              <div className="input-search">
                <Icon name="cal" />
                <input
                  id="nt-start"
                  className="input"
                  type="date"
                  value={startDate}
                  max={dueDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
            </div>

            <div className="pfield">
              <label htmlFor="nt-due">
                <Icon name="cal" />
                Due date
              </label>
              <div className="input-search">
                <Icon name="cal" />
                <input
                  id="nt-due"
                  className="input"
                  type="date"
                  value={dueDate}
                  min={startDate || undefined}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>

            <div className="pfield">
              <label htmlFor="nt-estimate">
                <Icon name="clock" />
                Estimate ({effortUnit})
              </label>
              <div className="input-search">
                <Icon name="clock" />
                <input
                  id="nt-estimate"
                  className="input"
                  type="number"
                  min="0"
                  step="0.5"
                  inputMode="decimal"
                  placeholder={effortUnit === "hours" ? "e.g. 8" : "e.g. 3"}
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                />
              </div>
            </div>

            <div className="pfield">
              <label htmlFor="nt-tag-input">
                <Icon name="star" />
                Tags
              </label>
              <TagChipInput
                id="nt-tag-input"
                names={tagNames}
                onNamesChange={setTagNames}
                draft={tagDraft}
                onDraftChange={setTagDraft}
                // Color comes from an existing tag if one matches by name;
                // unknown chips render with a muted dot until create-tag
                // assigns a real color server-side.
                colorFor={(name) =>
                  (tags.data?.tags ?? []).find(
                    (t) => t.name.toLowerCase() === name.toLowerCase(),
                  )?.color ?? "var(--border-strong)"
                }
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
